import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import TuneIcon from '@mui/icons-material/Tune';

import RoundedPaper from 'shared/components/RoundedPaper';
import useActorName from 'shared/hooks/useActorName';
import SeoGrowthService from 'services/SeoGrowthService';
import DecisionDetail from './components/DecisionDetail';
import LearningPanel from './components/LearningPanel';
import RulesPanel from './components/RulesPanel';
import RunReportView from './components/RunReportView';
import {
  VerdictChip,
  formatDay,
  formatTime,
  latestVerdict,
  nextReview,
} from './components/common';
import {
  ItemStatus,
  Learning,
  REGION_INFO,
  Region,
  Rule,
  RunReport,
  RunSummary,
  SeoItem,
  statusLabel,
  SeoSettings,
  TypeStats,
} from './types';

const service = new SeoGrowthService();
const LIST_LABEL = {
  title_meta: 'Title rewrite',
  refresh: 'Refresh',
  merge: 'Merge',
};

type View = 'log' | 'approval' | 'runs' | 'rules' | 'learning';
// Waiting on a person: drafts to review, and drafts sent back for a redraft.
const NEEDS_APPROVAL: ItemStatus[] = ['proposed', 'changes_requested'];

const REGIONS = Object.keys(REGION_INFO) as Region[];

// The store comes from ?region= so a link can point at one country's queue.
function initialRegion(): Region {
  const r = new URLSearchParams(window.location.search).get('region');
  return r && (REGIONS as string[]).includes(r.toUpperCase())
    ? (r.toUpperCase() as Region)
    : 'US';
}

export default function SeoGrowth() {
  const [actor] = useActorName();
  const [region, setRegion] = useState<Region>(initialRegion);
  // A response for a store the page has since switched away from is dropped.
  const regionRef = useRef(region);
  regionRef.current = region;
  const [view, setView] = useState<View>('approval');
  const [items, setItems] = useState<SeoItem[] | null>(null);
  const [runs, setRuns] = useState<RunSummary[] | null>(null);
  const [rules, setRules] = useState<Rule[]>([]);
  const [learning, setLearning] = useState<Learning | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [runId, setRunId] = useState<string | null>(null);
  const [run, setRun] = useState<RunReport | null>(null);
  const [settings, setSettings] = useState<SeoSettings | null>(null);
  const [stats, setStats] = useState<TypeStats | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const load = useCallback(async () => {
    service.region = region;
    const [res, set, st, rn, rl, lr] = await Promise.all([
      service.listItems(),
      service.getSettings(),
      service.getStats(),
      service.listRuns(),
      service.listRules(),
      service.getLearning(),
    ]);
    if (regionRef.current !== region) return;
    setLoadError(!res || !rn);
    setItems(res?.items ?? []);
    setRuns(rn?.runs ?? []);
    if (set) setSettings(set.settings);
    if (st) setStats(st.stats.new_article ?? null);
    if (rl) setRules(rl.rules);
    if (lr) setLearning(lr);
  }, [region]);

  // Switching store: clear what was shown, keep the address in step.
  const switchRegion = (next: Region) => {
    if (next === region) return;
    setItems(null);
    setRuns(null);
    setRun(null);
    setRunId(null);
    setSelectedId(null);
    setSettings(null);
    setRegion(next);
    const url = new URL(window.location.href);
    if (next === 'US') url.searchParams.delete('region');
    else url.searchParams.set('region', next);
    window.history.replaceState(null, '', url.toString());
  };

  useEffect(() => {
    load();
  }, [load]);

  // The run report is fetched when one is opened (the list is a summary).
  const shownRunId = runId ?? runs?.[0]?.id ?? null;
  useEffect(() => {
    if (view !== 'runs' || !shownRunId) return;
    setRun(null);
    service.region = region;
    let stale = false;
    service.getRun(shownRunId).then(r => !stale && setRun(r?.run ?? null));
    return () => {
      stale = true;
    };
  }, [view, shownRunId, region]);

  const visible = useMemo(() => {
    const all = items ?? [];
    const list =
      view === 'approval'
        ? all.filter(i => NEEDS_APPROVAL.includes(i.status))
        : all;
    return [...list].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  }, [items, view]);
  const selected = visible.find(i => i.id === selectedId) ?? visible[0] ?? null;
  const waiting = (items ?? []).filter(i => i.status === 'proposed').length;

  const replace = (next: SeoItem) =>
    setItems(prev => (prev ?? []).map(i => (i.id === next.id ? next : i)));
  const openItem = (id: string) => {
    setSelectedId(id);
    setView('log');
  };
  const openRun = (id: string) => {
    setRunId(id);
    setView('runs');
  };
  const addRule = async (text: string) => {
    const rule = await service.addRule(text, actor);
    setRules(prev => [...prev, rule]);
  };

  const showItems = view === 'log' || view === 'approval';

  return (
    <Box sx={{ p: 3 }}>
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
            SEO Decision Log
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ maxWidth: 820 }}
          >
            {region === 'US'
              ? 'Every morning the system reads Dosaze’s US search data, decides what to do, and records each decision: what it saw, why, the exact change, what it expects to happen, and when it will check. New articles wait here for approval; approving publishes to the blog. Title and description rewrites apply on their own and can be undone from their record.'
              : `Every morning the system writes an article for the Dosaze blog in ${REGION_INFO[region].country}: first adaptations of our best US articles, then topics from ${REGION_INFO[region].country}’s own searches. It recommends only products sold there and links only to that store. Each article waits here for approval.`}
          </Typography>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={region}
            onChange={(_, v) => v && switchRegion(v)}
            sx={{ mt: 1.5 }}
          >
            {REGIONS.map(r => (
              <ToggleButton key={r} value={r} sx={{ px: 1.5 }}>
                {REGION_INFO[r].label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
        <Button
          startIcon={<TuneIcon />}
          onClick={() => setSettingsOpen(true)}
          disabled={!settings}
          sx={{ flexShrink: 0 }}
        >
          Settings
        </Button>
      </Stack>

      <Tabs
        value={view}
        onChange={(_, v) => setView(v)}
        variant="scrollable"
        allowScrollButtonsMobile
        sx={{ mb: 2 }}
      >
        <Tab value="approval" label={`Needs approval (${waiting})`} />
        <Tab value="log" label={`Decision log (${items?.length ?? 0})`} />
        <Tab value="runs" label="Daily runs" />
        <Tab
          value="rules"
          label={`Rules (${rules.filter(r => r.active).length})`}
        />
        <Tab value="learning" label="What it’s learned" />
      </Tabs>

      {loadError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Couldn’t load the decision log. Refresh to try again.
        </Alert>
      )}
      {items === null && <CircularProgress />}

      {showItems && items !== null && !visible.length && !loadError && (
        <Typography color="text.secondary">
          {view === 'approval'
            ? 'Nothing waiting for approval. New drafts arrive each morning.'
            : 'No decisions yet.'}
        </Typography>
      )}

      {showItems && selected && (
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems="flex-start"
        >
          <RoundedPaper
            sx={{ width: { xs: '100%', md: 340 }, flexShrink: 0, p: 0 }}
          >
            <List dense disablePadding>
              {visible.map(i => {
                const next = nextReview(i);
                return (
                  <ListItemButton
                    key={i.id}
                    selected={i.id === selected.id}
                    onClick={() => setSelectedId(i.id)}
                  >
                    <ListItemText
                      primary={i.draft.title}
                      secondary={
                        <>
                          {i.opportunity.type === 'new_article'
                            ? `“${i.opportunity.keyword}” · ${i.opportunity.monthlySearches.toLocaleString()}/mo`
                            : `${LIST_LABEL[i.opportunity.type]} · ${i.opportunity.articleTitle}`}{' '}
                          · {statusLabel(i)}
                          {next
                            ? ` · next check ${formatDay(next.dueDate)}`
                            : ''}
                          {i.validation.warnings.length
                            ? ` · ${i.validation.warnings.length} warning(s)`
                            : ''}
                        </>
                      }
                    />
                    <VerdictChip verdict={latestVerdict(i)} />
                  </ListItemButton>
                );
              })}
            </List>
          </RoundedPaper>
          <RoundedPaper sx={{ flex: 1, p: 2, minWidth: 0 }}>
            <DecisionDetail
              item={selected}
              actor={actor}
              onApprove={async item =>
                replace(await service.approve(item.id, actor))
              }
              onReject={async (item, reason) =>
                replace(await service.reject(item.id, actor, reason))
              }
              onRequestChanges={async (item, feedback) =>
                replace(await service.requestChanges(item.id, actor, feedback))
              }
              onUndo={async item => replace(await service.undo(item.id, actor))}
              onAsk={async (item, question) => {
                const res = await service.askAboutItem(
                  item.id,
                  question,
                  actor,
                );
                replace(res.item);
                return res.item.thread;
              }}
              onAddRule={addRule}
              onOpenRun={openRun}
            />
          </RoundedPaper>
        </Stack>
      )}

      {view === 'runs' && runs !== null && (
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems="flex-start"
        >
          <RoundedPaper
            sx={{ width: { xs: '100%', md: 300 }, flexShrink: 0, p: 0 }}
          >
            {!runs.length && (
              <Typography color="text.secondary" sx={{ p: 2 }}>
                No runs yet. The first report appears after the next morning
                run.
              </Typography>
            )}
            <List dense disablePadding>
              {runs.map(r => (
                <ListItemButton
                  key={r.id}
                  selected={r.id === shownRunId}
                  onClick={() => setRunId(r.id)}
                >
                  <ListItemText
                    primary={formatTime(r.startedAt)}
                    secondary={`${r.drafted} drafted · ${r.titleRewrites ?? 0} title rewrites · ${r.reviews.length} reviews${
                      r.failed.length ? ` · ${r.failed.length} failed` : ''
                    }`}
                  />
                </ListItemButton>
              ))}
            </List>
          </RoundedPaper>
          {shownRunId && (
            <RoundedPaper sx={{ flex: 1, p: 2, minWidth: 0 }}>
              {run ? (
                <RunReportView
                  run={run}
                  onOpenItem={openItem}
                  onAsk={async question => {
                    const res = await service.askAboutRun(
                      run.id,
                      question,
                      actor,
                    );
                    setRun(res.run);
                    return res.run.thread;
                  }}
                  onAddRule={addRule}
                />
              ) : (
                <CircularProgress />
              )}
            </RoundedPaper>
          )}
        </Stack>
      )}

      {view === 'rules' && (
        <RulesPanel
          rules={rules}
          onAdd={addRule}
          onToggle={async (rule, active) => {
            const next = await service.updateRule(rule.id, { active });
            setRules(prev => prev.map(r => (r.id === next.id ? next : r)));
          }}
        />
      )}

      {view === 'learning' &&
        (learning ? (
          <LearningPanel learning={learning} />
        ) : (
          <Typography color="text.secondary">
            Couldn’t load what it has learned.
          </Typography>
        ))}

      {settings && (
        <SettingsDialog
          key={region}
          region={region}
          open={settingsOpen}
          settings={settings}
          stats={stats}
          onClose={() => setSettingsOpen(false)}
          onSaved={setSettings}
        />
      )}
    </Box>
  );
}

function SettingsDialog({
  region,
  open,
  settings,
  stats,
  onClose,
  onSaved,
}: {
  region: Region;
  open: boolean;
  settings: SeoSettings;
  stats: TypeStats | null;
  onClose: () => void;
  onSaved: (s: SeoSettings) => void;
}) {
  const [draftsPerDay, setDraftsPerDay] = useState(
    String(settings.draftsPerDay),
  );
  const [autoPublish, setAutoPublish] = useState(
    settings.autoPublish.new_article,
  );
  const [titlesPerDay, setTitlesPerDay] = useState(
    String(settings.titleRewritesPerDay),
  );
  const [autoApplyTitles, setAutoApplyTitles] = useState(
    settings.autoPublish.title_meta,
  );
  const [refreshesPerDay, setRefreshesPerDay] = useState(
    String(settings.refreshesPerDay),
  );
  const [autoApplyRefreshes, setAutoApplyRefreshes] = useState(
    settings.autoPublish.refresh,
  );
  const [mergesPerDay, setMergesPerDay] = useState(
    String(settings.mergesPerDay),
  );
  const [autoApplyMerges, setAutoApplyMerges] = useState(
    settings.autoPublish.merge,
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The international stores get new articles only.
  const isUS = region === 'US';

  useEffect(() => {
    if (open) {
      setDraftsPerDay(String(settings.draftsPerDay));
      setAutoPublish(settings.autoPublish.new_article);
      setTitlesPerDay(String(settings.titleRewritesPerDay));
      setAutoApplyTitles(settings.autoPublish.title_meta);
      setRefreshesPerDay(String(settings.refreshesPerDay));
      setAutoApplyRefreshes(settings.autoPublish.refresh);
      setMergesPerDay(String(settings.mergesPerDay));
      setAutoApplyMerges(settings.autoPublish.merge);
      setError(null);
    }
  }, [open, settings]);

  const decided = stats ? stats.published + stats.rejected : 0;
  const cleanRate =
    stats && decided
      ? Math.round((stats.publishedWithoutChanges / decided) * 100)
      : null;

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      onSaved(
        await service.saveSettings(
          isUS
            ? {
                draftsPerDay: Number(draftsPerDay),
                titleRewritesPerDay: Number(titlesPerDay),
                refreshesPerDay: Number(refreshesPerDay),
                mergesPerDay: Number(mergesPerDay),
                autoPublish: {
                  new_article: autoPublish,
                  title_meta: autoApplyTitles,
                  refresh: autoApplyRefreshes,
                  merge: autoApplyMerges,
                },
              }
            : {
                draftsPerDay: Number(draftsPerDay),
                autoPublish: {
                  new_article: autoPublish,
                } as SeoSettings['autoPublish'],
              },
        ),
      );
      onClose();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Settings: {REGION_INFO[region].label}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            id="seo-drafts-per-day"
            type="number"
            label="New drafts per day"
            inputProps={{ min: 0, max: 10 }}
            value={draftsPerDay}
            onChange={e => setDraftsPerDay(e.target.value)}
            helperText="Each draft costs roughly $0.10–0.20."
          />
          <Box>
            <FormControlLabel
              control={
                <Switch
                  id="seo-auto-publish"
                  checked={autoPublish}
                  onChange={e => setAutoPublish(e.target.checked)}
                />
              }
              label="Auto-publish new articles"
            />
            <Typography variant="body2" color="text.secondary">
              When on, drafts that pass every check with no warnings publish
              without review.
            </Typography>
            <Stack
              direction="row"
              spacing={1}
              sx={{ mt: 1, flexWrap: 'wrap', rowGap: 1 }}
            >
              <Chip size="small" label={`${decided} decided`} />
              <Chip
                size="small"
                color={
                  cleanRate !== null && cleanRate >= 90 ? 'success' : 'default'
                }
                label={
                  cleanRate === null
                    ? 'No approval history yet'
                    : `${cleanRate}% approved without changes`
                }
              />
            </Stack>
          </Box>
          {isUS && (
            <>
              <TextField
                id="seo-titles-per-day"
                type="number"
                label="Title & description rewrites per day"
                inputProps={{ min: 0, max: 10 }}
                value={titlesPerDay}
                onChange={e => setTitlesPerDay(e.target.value)}
                helperText="Existing articles Google shows but few people click. A few cents each."
              />
              <Box>
                <FormControlLabel
                  control={
                    <Switch
                      id="seo-auto-apply-titles"
                      checked={autoApplyTitles}
                      onChange={e => setAutoApplyTitles(e.target.checked)}
                    />
                  }
                  label="Apply title rewrites automatically"
                />
                <Typography variant="body2" color="text.secondary">
                  When on, rewrites with no warnings go live without review;
                  each one can be undone from its decision record. Only the
                  Google title and description change, never the article.
                </Typography>
              </Box>
              <TextField
                id="seo-refreshes-per-day"
                type="number"
                label="Article refreshes per day"
                inputProps={{ min: 0, max: 5 }}
                value={refreshesPerDay}
                onChange={e => setRefreshesPerDay(e.target.value)}
                helperText="Existing articles just below Google’s top spots, rewritten for approval. No more than 3 wait at once. About $0.20 each."
              />
              <Box>
                <FormControlLabel
                  control={
                    <Switch
                      id="seo-auto-apply-refreshes"
                      checked={autoApplyRefreshes}
                      onChange={e => setAutoApplyRefreshes(e.target.checked)}
                    />
                  }
                  label="Apply refreshes automatically"
                />
                <Typography variant="body2" color="text.secondary">
                  Off by default: each refresh waits for approval. When on,
                  refreshes with no warnings go live on their own and can be
                  undone.
                </Typography>
              </Box>
              <TextField
                id="seo-merges-per-day"
                type="number"
                label="Duplicate merges per day"
                inputProps={{ min: 0, max: 5 }}
                value={mergesPerDay}
                onChange={e => setMergesPerDay(e.target.value)}
                helperText="Groups of our own articles competing for the same searches, folded into one for approval. No more than 3 wait at once. About $0.25 each."
              />
              <Box>
                <FormControlLabel
                  control={
                    <Switch
                      id="seo-auto-apply-merges"
                      checked={autoApplyMerges}
                      onChange={e => setAutoApplyMerges(e.target.checked)}
                    />
                  }
                  label="Apply merges automatically"
                />
                <Typography variant="body2" color="text.secondary">
                  Off by default: each merge waits for approval. When on, merges
                  of up to 5 pages with no warnings go live on their own and can
                  be undone; bigger merges always wait for approval.
                </Typography>
              </Box>
            </>
          )}
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={save} disabled={saving}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
